import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Typography, Space, message, Upload, Spin } from 'antd';
import { UserOutlined, UploadOutlined, SaveOutlined } from '@ant-design/icons';
import { useProfile } from '../contexts/ProfileContext';

const { Title } = Typography;

const UserProfile = () => {
  const [form] = Form.useForm();
  const [profileImage, setProfileImage] = useState(null);
  const { profileData, loading, fetchProfile, updateProfile } = useProfile();

  useEffect(() => {
    if (!profileData) {
      fetchProfile().catch(error => {
        console.error('Error fetching user profile:', error);
        message.error('Failed to fetch user profile');
      });
    } else {
      form.setFieldsValue(profileData);
      setProfileImage(profileData.profile_image);
    }
  }, [profileData, form, fetchProfile]);

  const handleSubmit = async (values) => {
    try {
      await updateProfile({
        ...values,
        profile_image: profileImage
      });
      message.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile');
    }
  };

  const handleImageUpload = ({ file }) => {
    const isImage = file.type.startsWith('image/');
    const isLessThan2M = file.size / 1024 / 1024 < 2;
    
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }
    
    if (!isLessThan2M) {
      message.error('Image must be smaller than 2MB!');
      return false;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setProfileImage(e.target.result);
      message.success('Image uploaded successfully! Click "Save Changes" to save.');
    };
    reader.onerror = () => {
      message.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
    return false; // Prevent auto upload
  };

  return (
    <Card className="user-profile-container">
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Title level={2}>
          <Space>
            <UserOutlined />
            User Profile
          </Space>
        </Title>

        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ maxWidth: 600 }}
          >
            <Form.Item
              label="Profile Image"
              name="profile_image"
            >
              <Upload
                accept="image/*"
                showUploadList={false}
                customRequest={handleImageUpload}
              >
                <Space direction="vertical" align="center">
                  {profileImage && (
                    <img
                      src={profileImage}
                      alt="Profile"
                      style={{
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        objectFit: 'cover',
                        marginBottom: 8
                      }}
                    />
                  )}
                  <Button icon={<UploadOutlined />}>Upload Photo</Button>
                </Space>
              </Upload>
            </Form.Item>

            <Form.Item
              label="Username"
              name="username"
            >
              <Input disabled />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Phone"
              name="phone"
              rules={[
                { required: true, message: 'Please input your phone number!' }
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
              >
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Space>
    </Card>
  );
};

export default UserProfile;
